import type { WritingTaskV2, WritingCorrectionResultV2, WritingSection, WritingErrorCategoryV2, WritingErrorSeverityV2 } from "@/types/writingV2";

function buildGeminiErrorMessage(status: number, statusText: string, responseText: string): string {
  console.error("[EE_V2_GEMINI_API_ERROR_RAW]", {
    status,
    statusText,
    responseText,
  });

  if (status === 503) {
    return "A IA está temporariamente sobrecarregada no momento. Aguarde alguns instantes e tente novamente.";
  }

  if (status === 429) {
    return "A IA atingiu o limite temporário de uso. Aguarde alguns instantes e tente novamente.";
  }

  if (status === 401 || status === 403) {
    return "A chave da IA não foi aceita. Verifique a configuração da VITE_GEMINI_API_KEY no Vercel.";
  }

  if (status === 404) {
    return "O modelo de IA configurado não foi encontrado. Verifique se o modelo está definido como gemini-2.5-flash.";
  }

  return `Erro na API Gemini: ${status} ${statusText} - ${String(responseText || "").slice(0, 1000)}`;
}

async function callGeminiWithRetry({
  prompt,
  apiKey,
}: {
  prompt: string;
  apiKey: string;
}) {
  const primaryModel = "gemini-2.5-flash";
  const fallbackModel = "gemini-2.0-flash";
  const retryDelays = [1000, 2000, 4000];

  const makeCall = async (model: string) => {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`;
    console.log("[EE_V2_GEMINI_MODEL_USED]", { model });
    
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-goog-api-key": apiKey,
      },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }]
      })
    });
    
    const responseText = await response.text();
    return { ok: response.ok, status: response.status, statusText: response.statusText, text: responseText };
  };

  // Try primary model
  for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
    const result = await makeCall(primaryModel);

    if (result.ok) {
      return result.text;
    }

    if ((result.status === 503 || result.status === 429) && attempt < retryDelays.length) {
      console.warn("[EE_V2_GEMINI_RETRYABLE_ERROR]", {
        status: result.status,
        attempt: attempt + 1,
        nextDelayMs: retryDelays[attempt],
        responseText: result.text.slice(0, 1000),
      });

      await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
      continue;
    }

    // If we reach here, it failed all retries OR it's not a 503 error.
    
    // If it was a 503 on the last attempt, try fallback
    if (result.status === 503) {
      console.warn("[EE_V2_GEMINI_FALLBACK_MODEL_START]", {
        from: primaryModel,
        to: fallbackModel,
      });
      
      const fallbackResult = await makeCall(fallbackModel);
      if (fallbackResult.ok) {
        return fallbackResult.text;
      }
      
      throw new Error(buildGeminiErrorMessage(fallbackResult.status, fallbackResult.statusText, fallbackResult.text));
    }

    // For other errors (429, 401, etc.) or if fallback failed
    throw new Error(buildGeminiErrorMessage(result.status, result.statusText, result.text));
  }
  
  throw new Error("Erro desconhecido ao chamar a Gemini.");
}

export async function correctWritingSectionAV2(task: WritingTaskV2): Promise<WritingCorrectionResultV2> {
  if (task.section !== "Section A") {
    throw new Error("A correção da Section B ainda não foi implementada na EE v2.");
  }

  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

  if (!apiKey) {
    throw new Error("Chave VITE_GEMINI_API_KEY não encontrada no ambiente.");
  }

  console.log("[EE_V2_GEMINI_ENV_CHECK]", {
    hasApiKey: Boolean(apiKey),
    apiKeyPrefix: apiKey ? String(apiKey).slice(0, 6) : null,
  });

  const prompt = `Você é um corretor pedagógico de francês para estudantes que treinam a Expression Écrite do TEF Canada.

IMPORTANTE:
Você NÃO deve atribuir nota.
Você NÃO deve atribuir NCLC.
Você NÃO deve atribuir CEFR.
Você NÃO deve simular uma correção oficial.
Você NÃO deve dizer se o aluno está aprovado ou reprovado.
Você deve fazer apenas uma correção pedagógica linguística.

Objetivo:
Identificar erros linguísticos no texto do aluno, especialmente:
- gramática;
- preposições;
- conjugação verbal;
- concordância;
- ortografia;
- pontuação;
- sintaxe;
- pronomes;
- conectores;
- vocabulário inadequado;
- naturalidade.

Para cada erro, retorne:
- categoria;
- severidade;
- trecho original;
- trecho corrigido;
- frase de contexto onde o erro aparece;
- explicação curta e pedagógica em português;
- sugestão opcional de flashcard.

Regras:
- A frase de contexto deve ser apenas a frase onde o erro aparece, nunca a redação inteira.
- Preserve o sentido original do aluno.
- Não reescreva o texto com ideias novas.
- Corrija apenas língua, clareza e naturalidade.
- A Section A tem mínimo recomendado de 80 palavras.
- Se o texto tiver menos de 80 palavras, indique isso como observação pedagógica, mas não dê nota.

Responda SOMENTE com JSON válido.
Não use markdown.
Não use bloco de código.
Não escreva nada fora do JSON.

Formato obrigatório:
{
  "correctionType": "linguistic_feedback",
  "isOfficialEvaluation": false,
  "section": "Section A",
  "wordCount": number,
  "minimumWordsRequired": 80,
  "meetsMinimumWordCount": boolean,
  "generalFeedback": "string",
  "mainFocusAreas": ["Gramática", "Preposição"],
  "errors": [
    {
      "id": "err-001",
      "section": "Section A",
      "category": "Gramática",
      "severity": "medium",
      "original": "string",
      "corrected": "string",
      "contextSentence": "string",
      "explanation": "string",
      "flashcardSuggestion": {
        "front": "string",
        "back": "string"
      }
    }
  ],
  "correctedVersion": "string",
  "studySuggestions": ["string"],
  "source": "gemini",
  "correctedAt": "ISO_DATE"
}

Texto da tarefa:
TÍTULO:
${task.title || "Sem título"}

INSTRUÇÃO:
${task.prompt}

TEXTO DO ALUNO:
${task.userText}`;

  try {
    const responseText = await callGeminiWithRetry({ prompt, apiKey });
    console.log("[EE_V2_AI_RAW_RESPONSE_TEXT]", responseText.slice(0, 5000));

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (error) {
      console.error("[WRITING_V2_AI_RESPONSE_JSON_PARSE_ERROR]", error);
      throw new Error("A resposta da API Gemini não é JSON válido.");
    }

    const generatedText =
      data?.candidates?.[0]?.content?.parts
        ?.map((part: any) => part.text || "")
        .join("\n")
      || data?.candidates?.[0]?.content?.parts?.[0]?.text
      || "";

    console.log("[EE_V2_AI_GENERATED_TEXT]", generatedText.slice(0, 5000));

    if (!generatedText || generatedText.trim().length < 20) {
      throw new Error("A Gemini respondeu, mas não retornou texto de correção.");
    }

    let rawCorrection;
    try {
      rawCorrection = extractJsonFromAiText(generatedText);
    } catch (error) {
      console.error("[WRITING_V2_AI_CORRECTION_PARSE_ERROR]", error);
      throw error;
    }

    return normalizeWritingV2Correction(rawCorrection, task);

  } catch (error) {
    console.error("[WRITING_V2_AI_SERVICE_ERROR]", error);
    throw error;
  }
}

export async function correctWritingSectionBV2(task: WritingTaskV2): Promise<WritingCorrectionResultV2> {
  if (task.section !== "Section B") {
    throw new Error("A função correctWritingSectionBV2 aceita apenas tarefas da Section B.");
  }

  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;

  if (!apiKey) {
    throw new Error("Chave VITE_GEMINI_API_KEY não encontrada no ambiente.");
  }

  const prompt = `Você é um corretor pedagógico de francês para estudantes que treinam a Section B da Expression Écrite do TEF Canada.

IMPORTANTE:
Você NÃO deve atribuir nota.
Você NÃO deve atribuir score.
Você NÃO deve atribuir NCLC.
Você NÃO deve atribuir CEFR.
Você NÃO deve simular uma correção oficial.
Você NÃO deve dizer se o aluno está aprovado ou reprovado.

Sua função é apenas corrigir linguisticamente e pedagogicamente o texto do aluno.

A Section B exige uma resposta argumentativa, geralmente uma carta ou texto de opinião. O aluno deve apresentar uma posição clara e desenvolver argumentos com exemplos.

Foque em:
- gramática;
- preposições;
- conjugação verbal;
- concordância de gênero e número;
- ortografia;
- pontuação;
- sintaxe;
- pronomes;
- conectores;
- vocabulário inadequado;
- naturalidade;
- clareza da opinião;
- organização dos argumentos;
- coerência entre introdução, desenvolvimento e conclusão;
- uso de exemplos concretos;
- registro adequado.

Para cada erro, retorne:
- categoria;
- severidade;
- trecho original;
- trecho corrigido;
- frase de contexto onde o erro aparece;
- explicação curta em português;
- sugestão opcional de flashcard.

Regras:
- A frase de contexto deve ser apenas a frase onde o erro aparece.
- Não coloque a redação inteira como contexto.
- Preserve as ideias do aluno.
- Não invente argumentos novos.
- Corrija apenas língua, clareza, estrutura e naturalidade.
- A Section B tem mínimo recomendado de 200 palavras.
- Se o texto tiver menos de 200 palavras, mencione isso no feedback pedagógico, mas não dê nota.

Responda SOMENTE com JSON válido.
Não use markdown.
Não use bloco de código.
Não escreva nada fora do JSON.

Formato obrigatório:
{
  "correctionType": "linguistic_feedback",
  "isOfficialEvaluation": false,
  "section": "Section B",
  "wordCount": number,
  "minimumWordsRequired": 200,
  "meetsMinimumWordCount": boolean,
  "generalFeedback": "string",
  "mainFocusAreas": ["Gramática", "Preposição"],
  "errors": [
    {
      "id": "err-001",
      "section": "Section B",
      "category": "Gramática",
      "severity": "medium",
      "original": "string",
      "corrected": "string",
      "contextSentence": "string",
      "explanation": "string",
      "flashcardSuggestion": {
        "front": "string",
        "back": "string"
      }
    }
  ],
  "correctedVersion": "string",
  "studySuggestions": ["string"],
  "source": "gemini",
  "correctedAt": "ISO_DATE"
}

DADOS DA TAREFA:
Título:
${task.title || "Sem título"}

Instrução:
${task.prompt}

Texto do aluno:
${task.userText}`;

  try {
    const responseText = await callGeminiWithRetry({ prompt, apiKey });
    console.log("[EE_V2_AI_RAW_RESPONSE_TEXT_B]", responseText.slice(0, 5000));

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (error) {
      console.error("[WRITING_V2_AI_RESPONSE_JSON_PARSE_ERROR_B]", error);
      throw new Error("A resposta da API Gemini não é JSON válido.");
    }

    const generatedText =
      data?.candidates?.[0]?.content?.parts
        ?.map((part: any) => part.text || "")
        .join("\n")
      || data?.candidates?.[0]?.content?.parts?.[0]?.text
      || "";

    console.log("[EE_V2_AI_GENERATED_TEXT_B]", generatedText.slice(0, 5000));

    if (!generatedText || generatedText.trim().length < 20) {
      throw new Error("A Gemini respondeu, mas não retornou texto de correção.");
    }

    let rawCorrection;
    try {
      rawCorrection = extractJsonFromAiText(generatedText);
    } catch (error) {
      console.error("[WRITING_V2_AI_CORRECTION_PARSE_ERROR_B]", error);
      throw error;
    }

    return normalizeWritingV2Correction(rawCorrection, task);

  } catch (error) {
    console.error("[WRITING_V2_AI_SERVICE_ERROR_B]", error);
    throw error;
  }
}

function extractJsonFromAiText(text: string) {
  const cleaned = String(text || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

  try {
    return JSON.parse(cleaned);
  } catch {}

  const firstBrace = cleaned.indexOf("{");
  const lastBrace = cleaned.lastIndexOf("}");

  if (firstBrace >= 0 && lastBrace > firstBrace) {
    return JSON.parse(cleaned.slice(firstBrace, lastBrace + 1));
  }

  throw new Error("Não foi possível extrair JSON da resposta da IA.");
}

function normalizeWritingV2Correction(raw: any, task: WritingTaskV2): WritingCorrectionResultV2 {
  const allowedCategories: WritingErrorCategoryV2[] = [
    "Gramática",
    "Preposição",
    "Conjugação",
    "Concordância",
    "Ortografia",
    "Pontuação",
    "Vocabulário",
    "Sintaxe",
    "Naturalidade",
    "Conectores",
    "Pronomes",
    "Outro",
  ];

  const normalizeCategory = (category: any): WritingErrorCategoryV2 => {
    const value = String(category || "").trim() as WritingErrorCategoryV2;
    return allowedCategories.includes(value) ? value : "Outro";
  };

  const normalizeSeverity = (severity: any): WritingErrorSeverityV2 => {
    const value = String(severity || "").toLowerCase();
    if (value === "low" || value === "medium" || value === "high") return value as WritingErrorSeverityV2;
    return "medium";
  };

  const errors = Array.isArray(raw?.errors)
    ? raw.errors.map((error: any, index: number) => ({
        id: error?.id || `err-${String(index + 1).padStart(3, "0")}`,
        section: task.section as WritingSection,
        category: normalizeCategory(error?.category),
        severity: normalizeSeverity(error?.severity),
        original: String(error?.original || ""),
        corrected: String(error?.corrected || ""),
        contextSentence: String(error?.contextSentence || error?.context || ""),
        explanation: String(error?.explanation || ""),
        flashcardSuggestion: error?.flashcardSuggestion
          ? {
              front: String(error.flashcardSuggestion.front || ""),
              back: String(error.flashcardSuggestion.back || ""),
            }
          : undefined,
      }))
    : [];

  const mainFocusAreas = Array.isArray(raw?.mainFocusAreas)
    ? raw.mainFocusAreas.map(normalizeCategory).slice(0, 5)
    : [];

  return {
    correctionType: "linguistic_feedback",
    isOfficialEvaluation: false,
    section: task.section,
    wordCount: task.wordCount,
    minimumWordsRequired: task.minimumWordsRequired,
    meetsMinimumWordCount: task.meetsMinimumWordCount,
    generalFeedback: String(raw?.generalFeedback || ""),
    mainFocusAreas,
    errors,
    correctedVersion: String(raw?.correctedVersion || ""),
    studySuggestions: Array.isArray(raw?.studySuggestions)
      ? raw.studySuggestions.map((item: any) => String(item))
      : [],
    source: "gemini",
    correctedAt: new Date().toISOString(),
  };
}
